import React, { useEffect, useState } from 'react'
import { uniq } from 'lodash'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { StyledFieldSet, StyledLegend, StyledSpan } from 'components/StyledElements'
import { Grid, Button, Stack } from '@mui/material'
import Select from 'components/forms/Select'
import DtoHydrometricStation from 'hydrometry/dto/DtoHydrometricStation'
import { hasValue } from 'utils/NumberUtil'
import Input from 'components/forms/Input'
import RadioButtons from '../../../components/forms/RadioButtons'
import { CardTable } from '../../../components/datatable/NewTable'
import { exportFile } from '../../../utils/ExportDataUtil'
import { readImportCsvStationsCodes } from '../../../import/components/utils/ImportUtils'
import { CITIES, STATIONS, DEPARTMENTS, VALID_LENGHT } from 'traitements/constants/TraitementConstants'
import { Warning } from '@mui/icons-material'
import { shallowEqual, useSelector } from 'react-redux'


const AddHydroStepOptionsTreatmentOndes = ({
    filters,
    changeFilters,
    newJob,
    setNewJob,
    listStation,
    setListStation,
}) => {
    const {
        cities,
    } = useSelector(store => ({
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
        cities: store.CityReducer.cities,
    }), shallowEqual)
    useEffect(() => {
        if (!filters?.stationsMode) {
            changeFilters({ stationsMode: CITIES })
        }
    }, [])


    const codesTypes = [
        { code: CITIES, name: i18n.cities },
        { code: STATIONS, name: i18n.stations },
        { code: DEPARTMENTS, name: i18n.departments },
    ]

    const [newCode, setNewCode] = useState('')
    const currentList = listStation.find(([key]) => key === filters.stationsMode)?.[1] ?? []

    const stationsCodes = currentList.map(hydroCode => ({ hydroCode }))

    const onDeleteHydroCode = ({ hydroCode }) => {
        const key = filters.stationsMode === DEPARTMENTS ? DEPARTMENTS : filters.stationsMode
        setListStation(prev =>
            prev.map(item => item[0] === key ? [item[0], item[1].filter(code => code !== hydroCode)] : item,
            ),
        )
    }

    const changeJob = (changes) => setNewJob({ ...newJob, ...changes })

    const onChangeBssFile = e => {
        readImportCsvStationsCodes(e, { importFile: e.target }, (codes) => {
            const key = filters.stationsMode === DEPARTMENTS ? DEPARTMENTS : filters.stationsMode
            setListStation(prev =>
                prev.map(item =>
                    item[0] === key ? [item[0], uniq([...item[1], ...codes])]: item,
                ),
            )
        })
    }

    const handleAdd = () => {
        if (!newCode) return
        const key = filters.stationsMode === DEPARTMENTS ? DEPARTMENTS : filters.stationsMode
        setListStation(prev =>
            prev.map(item => {
                if (item[0] !== key) return item
                if (item[1].includes(newCode)) return item
                return [item[0], [...item[1], newCode]]
            }),
        )

        setNewCode('')
    }
    const isInputMode = [STATIONS, DEPARTMENTS].includes(filters.stationsMode)
    return (
        <>
            <StyledFieldSet>
                <StyledLegend>
                    <StyledSpan>{i18n.description}</StyledSpan>
                </StyledLegend>
                <Grid container mb={2} spacing={8}>
                    <Grid size={12}>
                        <Input
                            title={i18n.name}
                            obligatory
                            value={newJob.name}
                            onChange={v => changeJob({ name: v })}
                        />
                    </Grid>

                </Grid>
            </StyledFieldSet>
            <StyledFieldSet>
                <StyledLegend>
                    <StyledSpan>{i18n.options}</StyledSpan>
                </StyledLegend>
                <Grid container pb={2} spacing={6}>
                    <Grid size={4}>
                        <RadioButtons
                            elements={codesTypes}
                            selected={filters.stationsMode}
                            onChange={v => changeFilters({ stationsMode: v })}
                            title={i18n.schemeAgencyID}
                        />
                    </Grid>
                </Grid>
                <Grid container size={3} pb={2} spacing={6}>
                    <Grid size={6}>
                        {isInputMode ? (
                            <Input
                                id='txtAddHydroCode'
                                type='text'
                                value={newCode}
                                onChange={v => setNewCode(v)}
                                title={i18n.code}
                                obligatory
                                onEnterKeyPress={handleAdd}
                                noSpace
                            />
                        ) : (
                            <Select
                                label={i18n.city}
                                options={cities}
                                value={newCode}
                                onChange={v => setNewCode(v)}
                                keyValue='id'
                                displayWithCode
                            />
                        )}
                    </Grid>
                    <Grid container size={6} spacing={4}>
                        <Button
                            onClick={handleAdd}
                            variant='contained'
                        >
                            {i18n.add}
                        </Button>
                        <Button
                            variant='contained'
                            component='label'
                        >
                            {i18n.importLabelStation}
                            <input
                                type='file'
                                accept='.csv'
                                hidden
                                onChange={onChangeBssFile}
                            />
                        </Button>
                    </Grid>
                </Grid>
                <Grid container justifyContent='space-between' spacing={2}>
                    <Grid size={9}>
                        {filters?.stationsMode === STATIONS &&
                    currentList.map(code => {
                        const isInvalid = hasValue(code) && !VALID_LENGHT.includes(code.length)

                        if (!isInvalid) return null

                        return (
                            <Grid
                                key={code}
                                container
                                justifyContent='space-between'
                                alignItems='center'
                                spacing={2}
                            >
                                <Grid size={1}>
                                    <Warning sx={{ color: 'orange' }} />
                                </Grid>
                                <Grid size={11}>
                                    <h6>
                                        {i18n.invalidHydroCode} : {code}
                                    </h6>
                                </Grid>
                            </Grid>
                        )
                    })}
                    </Grid>
                </Grid>
                <Stack spacing={4}>
                    <CardTable
                        headers={['hydroCode']}
                        rows={stationsCodes}
                        title={ filters.stationsMode === STATIONS ? i18n.credentials : i18n[filters.stationsMode] }
                        lineActions={[
                            { icon: 'delete', onClick: onDeleteHydroCode },
                        ]}
                        actions={[{
                            icon: 'download',
                            onClick: () => exportFile({
                                data: [{ ...(stationsCodes[0]), headers: ['hydroCode'] }, ...(stationsCodes.slice(1, stationsCodes.length))],
                                titleFile: i18n.credentials,
                            }),
                            tooltip: i18n.download,
                        }]}
                        displayHeaders={false}
                    />
                </Stack>
            </StyledFieldSet>
        </>
    )
}

AddHydroStepOptionsTreatmentOndes.propTypes = {
    station: PropTypes.instanceOf(DtoHydrometricStation),
    filters: PropTypes.object,
    changeFilters: PropTypes.func,
    newJob: PropTypes.any,
    setNewJob: PropTypes.func,
    listStation: PropTypes.any,
    setListStation: PropTypes.func,
}

export default AddHydroStepOptionsTreatmentOndes
